import { useEffect, useState, useCallback } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { DockviewApi } from 'dockview-react';
import { Sidebar } from '@/components/layout/Sidebar';
import { DockLayout } from '@/components/layout/DockLayout';
import { AIPanel } from '@/components/layout/AIPanel';
import { WebHeader } from '@/components/layout/WebHeader';
import { useAuthStore } from '@/stores/auth-store';
import { WelcomeScreen } from '@/components/screens/WelcomeScreen';
import { cn } from '@/lib/utils';

function Dashboard() {
  const [dockApi, setDockApi] = useState<DockviewApi | null>(null);
  const [isAIPanelOpen, setIsAIPanelOpen] = useState(false);

  const handleDockReady = useCallback((api: DockviewApi) => {
    setDockApi(api);
  }, []);

  const togglePanel = useCallback(
    (panelId: string, title: string) => {
      if (panelId === 'ai') {
        setIsAIPanelOpen((prev) => !prev);
        return;
      }

      if (!dockApi) return;

      const existingPanel = dockApi.getPanel(panelId);
      if (existingPanel) {
        existingPanel.api.setActive();
      } else {
        const dashboardPanel = dockApi.getPanel('dashboard');
        dockApi.addPanel({
          id: panelId,
          component: panelId,
          title,
          position: dashboardPanel
            ? { referencePanel: dashboardPanel, direction: 'within' }
            : undefined,
        });
      }
    },
    [dockApi]
  );

  return (
    <div className="flex h-screen w-screen flex-col overflow-hidden bg-background">
      {/* Web header */}
      <WebHeader />

      {/* Main layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <Sidebar onTogglePanel={togglePanel} isAIPanelOpen={isAIPanelOpen} />

        {/* Dockview layout */}
        <div className="flex-1 relative overflow-hidden">
          <DockLayout onReady={handleDockReady} />
        </div>
      </div>

      {/* Floating AI Panel Overlay */}
      <div
        className={cn(
          'fixed top-14 right-0 bottom-0 w-[400px] border-l border-border shadow-2xl transition-transform duration-300 ease-out z-50 bg-surface',
          isAIPanelOpen ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        <AIPanel onClose={() => setIsAIPanelOpen(false)} />
      </div>

      {/* Backdrop when AI panel is open */}
      {isAIPanelOpen && (
        <div
          className="fixed inset-0 top-14 bg-black/20 z-40 lg:hidden"
          onClick={() => setIsAIPanelOpen(false)}
        />
      )}
    </div>
  );
}

function App() {
  const { isAuthenticated, isLoading, initialize } = useAuthStore();

  useEffect(() => {
    initialize();
  }, [initialize]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <div className="text-sm text-muted-foreground">Loading ChartZen...</div>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : <WelcomeScreen />
        }
      />
      <Route
        path="/dashboard"
        element={isAuthenticated ? <Dashboard /> : <Navigate to="/" replace />}
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
